import { Avatar, Box, Typography, makeStyles } from "@material-ui/core";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import dayjs from "dayjs";
import TruncatedText from "components/TruncatedText";
import type { MalwareFile } from "@dashboard-v3/api";
import { initials } from "../utils";

interface Props {
  file: MalwareFile;
  onClick: (file: MalwareFile) => void;
}

export default function FileRow({ file, onClick }: Props) {
  const classes = useStyles();

  return (
    <div
      className={classes.row}
      role="button"
      tabIndex={0}
      data-testid="malware-file"
      onClick={() => onClick(file)}
      onKeyDown={event => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick(file);
        }
      }}
    >
      <span className={classes.tree} />
      <div className={classes.fileIcon}>
        <InsertDriveFileOutlinedIcon fontSize="small" />
      </div>
      <Box minWidth={0}>
        <TruncatedText text={file.name} variant="body2" />
        <TruncatedText
          text={file.path}
          variant="caption"
          color="textSecondary"
          display="block"
        />
      </Box>
      <Box display="flex" alignItems="center" gridGap={10} minWidth={0}>
        <Avatar className={classes.avatar}>{initials(file.user)}</Avatar>
        <TruncatedText text={file.user} variant="body2" />
      </Box>
      <Typography variant="caption" color="textSecondary" noWrap>
        {dayjs(file.updatedAt).format("YYYY-MM-DD HH:mm")}
      </Typography>
      <ChevronRightIcon className={classes.chevron} />
    </div>
  );
}

const useStyles = makeStyles(theme => ({
  row: {
    display: "grid",
    gridTemplateColumns: "28px 32px minmax(0, 1.4fr) minmax(0, 1fr) auto 24px",
    alignItems: "center",
    gap: 12,
    padding: "10px 16px 10px 44px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: "pointer",
    "&:last-child": { borderBottom: 0 },
    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.03)" },
  },
  tree: {
    width: 28,
    height: 20,
    marginTop: -12,
    borderLeft: "1px solid rgba(0, 0, 0, 0.2)",
    borderBottom: "1px solid rgba(0, 0, 0, 0.2)",
    borderRadius: "0 0 0 4px",
  },
  fileIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
  },
  avatar: {
    width: 30,
    height: 30,
    fontSize: "0.78rem",
    backgroundColor: "#e3f4fb",
    color: "#04688a",
  },
  chevron: { color: theme.palette.text.secondary, justifySelf: "end" },
}));
